import { ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DollarSign, Eye, Loader2, Ticket } from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getTotalPrizes, TotalPrizesResponse } from "@/Api/totalPrizes";
import { Mask } from "@/utils/mask";
import {
  ClientResultsFilterState,
  FiltersBar,
  defaultFilters,
  filtersFromSearch,
  filtersToSearch,
  formatDateBr,
  lotteryColor,
} from "@/pages/ClientResults/shared";
import { DetailModal } from "./DetailModal";

const SummaryCard = ({
  title,
  value,
  icon,
  iconClassName,
}: {
  title: string;
  value: ReactNode;
  icon: ReactNode;
  iconClassName: string;
}) => (
  <Card>
    <CardContent className="flex items-center justify-between p-5">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
      </div>
      <div className={`flex h-12 w-12 items-center justify-center rounded-full ${iconClassName}`}>{icon}</div>
    </CardContent>
  </Card>
);

/**
 * Concursos > Premiações Totais (LOT-25).
 * Premiações já apuradas por concurso, ativos e desativados; concurso sem prêmio não aparece.
 * Datas vazias = todo o histórico.
 */
export const PageList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ClientResultsFilterState>(() => filtersFromSearch(searchParams));
  const [data, setData] = useState<TotalPrizesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailContestId, setDetailContestId] = useState<number | null>(null);

  const fetchData = async (f: ClientResultsFilterState, page = 1) => {
    if (f.date_start && f.date_end && f.date_start > f.date_end) {
      toast.error("A data final deve ser igual ou posterior à data inicial.");
      return;
    }
    setLoading(true);
    try {
      const { data: res, ok } = await getTotalPrizes({
        date_start: f.date_start || undefined,
        date_end: f.date_end || undefined,
        lottery_id: f.lottery_id.length ? f.lottery_id : undefined,
        contest_names: f.contest_names || undefined,
        page,
      });
      if (ok && res?.response) {
        setData(res.response);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Sem datas na URL: abre no mês atual. Para ver todo o histórico, basta apagar as datas.
    const hasDates = searchParams.has("date_start") || searchParams.has("date_end") || searchParams.has("all");
    const initial = hasDates
      ? { ...filters, date_start: searchParams.get("date_start") || "", date_end: searchParams.get("date_end") || "" }
      : filters;
    setFilters(initial);
    fetchData(initial, Number(searchParams.get("page")) || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (f: ClientResultsFilterState, page = 1) => {
    const search = new URLSearchParams(filtersToSearch(f));
    if (!f.date_start && !f.date_end) search.set("all", "1");
    if (page > 1) search.set("page", String(page));
    setSearchParams(search, { replace: true });
    fetchData(f, page);
  };

  const handleClear = () => {
    const f = defaultFilters();
    setFilters(f);
    apply(f);
  };

  const rows = data?.data ?? [];

  return (
    <Main>
      <div>
        <h2 className="text-2xl font-semibold">Premiações por concurso</h2>
        <p className="text-sm text-muted-foreground">Apuração de todos os bolões da lotérica</p>
      </div>

      <FiltersBar
        value={filters}
        onChange={setFilters}
        onApply={() => apply(filters)}
        onClear={handleClear}
        showSellerFilters={false}
        showSearch={false}
        loading={loading}
      />
      <p className="-mt-2 text-xs text-muted-foreground">Deixe as datas em branco para ver todo o histórico.</p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          title="Valor total das premiações"
          value={Mask.formatCurrency(data?.totals.total_prize ?? 0)}
          icon={<DollarSign className="h-6 w-6" />}
          iconClassName="bg-green-100 text-green-600 dark:bg-green-900/30"
        />
        <SummaryCard
          title="Prêmio total por cota"
          value={Mask.formatCurrency(data?.totals.total_prize_per_quota ?? 0)}
          icon={<DollarSign className="h-6 w-6" />}
          iconClassName="bg-green-100 text-green-600 dark:bg-green-900/30"
        />
        <SummaryCard
          title="Bilhetes premiados"
          value={data?.totals.awarded_tickets ?? 0}
          icon={<Ticket className="h-6 w-6" />}
          iconClassName="bg-blue-100 text-blue-600 dark:bg-blue-900/30"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-600 hover:bg-blue-600">
                <TableHead className="font-bold text-white">Loteria</TableHead>
                <TableHead className="font-bold text-white">Concurso</TableHead>
                <TableHead className="font-bold text-white">Data de validade</TableHead>
                <TableHead className="text-right font-bold text-white">Premiação total</TableHead>
                <TableHead className="text-right font-bold text-white">Prêmio total por cota</TableHead>
                <TableHead className="text-center font-bold text-white">Bilhetes premiados</TableHead>
                <TableHead className="text-center font-bold text-white">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                    Nenhum concurso com premiação neste filtro.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.contest_id}>
                    <TableCell className="font-bold" style={{ color: lotteryColor(row.lottery_color) }}>
                      {row.lottery_name}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{row.contest_name}</span>
                        {row.investigation_status === "partial" && (
                          <span
                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                            title={`${row.investigated_products} de ${row.total_products} bolões lançados na apuração`}
                          >
                            Apuração parcial
                          </span>
                        )}
                        {!row.is_active && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            Desativado
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDateBr(row.validation_date)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-green-700 dark:text-green-400">
                      {Mask.formatCurrency(row.prize_total)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Mask.formatCurrency(row.prize_per_quota_total)}
                    </TableCell>
                    <TableCell className="text-center font-semibold tabular-nums">{row.awarded_tickets}</TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        title="Ver bolões premiados"
                        onClick={() => setDetailContestId(row.contest_id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-start justify-between gap-2 text-xs text-muted-foreground">
        <div>
          <p>
            {data?.totals.total_contests ?? 0} {data?.totals.total_contests === 1 ? "concurso" : "concursos"} •
            Premiações dos bolões apurados na lotérica.
          </p>
          <p>Prêmio total por cota: soma do prêmio de uma cota de cada bolão do concurso.</p>
        </div>
        {data && data.last_page > 1 && (
          <div className="flex items-center gap-2">
            <span>
              Página {data.current_page} de {data.last_page}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || data.current_page <= 1}
              onClick={() => apply(filters, data.current_page - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || data.current_page >= data.last_page}
              onClick={() => apply(filters, data.current_page + 1)}
            >
              Próxima
            </Button>
          </div>
        )}
      </div>

      <DetailModal contestId={detailContestId} onClose={() => setDetailContestId(null)} />
    </Main>
  );
};
